import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Copy, Check, Send, Mail, Search, Flame, User, Loader2 } from "lucide-react";

function initial(name) {
    return (name || "P").trim().charAt(0).toUpperCase();
}

export default function InviteDialog({ open, onClose, group }) {
    const { t } = useTranslation();
    const [emails, setEmails] = useState("");
    const [message, setMessage] = useState("");
    const [link, setLink] = useState("");
    const [copied, setCopied] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [sending, setSending] = useState(false);

    // user search
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState([]); // array of {id,name,is_warrior,email}
    const debounceRef = useRef(null);

    useEffect(() => {
        if (open && group) {
            const origin = window.location.origin;
            setLink(`${origin}/group-prayers?invite=${group.id}`);
            setEmails("");
            setMessage("");
            setSent(false);
            setError("");
            setCopied(false);
            setQ("");
            setResults([]);
            setSelected([]);
        }
    }, [open, group]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (!q || q.trim().length < 2) { setResults([]); return; }
        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get("/users/search", { params: { q: q.trim(), limit: 8 } });
                setResults(data || []);
            } catch {
                setResults([]);
            } finally {
                setSearching(false);
            }
        }, 300);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [q]);

    if (!open || !group) return null;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            const el = document.createElement("textarea");
            el.value = link;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const pickUser = (u) => {
        if (selected.some((x) => x.id === u.id)) return;
        setSelected((s) => [...s, u]);
        setQ("");
        setResults([]);
    };

    const removeSelected = (id) => setSelected((s) => s.filter((x) => x.id !== id));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        const emailList = emails
            .split(/[,\s\n;]+/)
            .map((s) => s.trim())
            .filter((s) => /.+@.+\..+/.test(s));
        const userIds = selected.map((u) => u.id);
        if (emailList.length === 0 && userIds.length === 0) {
            setError("Pick at least one user or type an email to invite.");
            return;
        }
        setSending(true);
        try {
            await api.post(`/group-prayers/${group.id}/invite`, { emails: emailList, user_ids: userIds, message });
            setSent(true);
            setEmails("");
            setSelected([]);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            style={{ background: "rgba(30,27,75,0.42)" }}
            onClick={onClose}
            data-testid="invite-dialog-overlay"
        >
            <div
                className="w-full max-w-lg p-card p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="invite-dialog"
            >
                <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-[#F5F0E6]" data-testid="invite-close">
                    <X size={16} style={{ color: "#1E1B4B" }} />
                </button>
                <div className="text-xs tracking-[0.22em] uppercase" style={{ color: "#7F1D1D" }}>{group.title}</div>
                <h2 className="font-display text-3xl mt-1" style={{ color: "#1E1B4B" }}>{t("invite.title")}</h2>
                <p className="mt-2 text-sm" style={{ color: "#525252" }}>{t("invite.sub")}</p>

                <div className="mt-5">
                    <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("invite.link_label")}</label>
                    <div className="flex items-stretch gap-2">
                        <input readOnly value={link}
                               className="flex-1 px-4 py-2.5 rounded-full border text-sm"
                               style={{ borderColor: "#E5E0D8", background: "#F5F0E6" }}
                               data-testid="invite-link" />
                        <button onClick={copy} className="btn-gold text-sm inline-flex items-center gap-1.5" data-testid="invite-copy">
                            {copied ? <><Check size={14} /> {t("invite.copied")}</> : <><Copy size={14} /> {t("invite.copy")}</>}
                        </button>
                    </div>
                </div>

                <div className="gold-divider my-6" />

                <div>
                    <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>
                        <Search size={12} className="inline mr-1" /> Search app users by name, email or phone
                    </label>
                    <div className="relative">
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                               placeholder="Type at least 2 characters…"
                               className="w-full px-4 py-2.5 rounded-full border text-sm"
                               style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                               data-testid="invite-search-input" />
                        {searching && (
                            <Loader2 className="animate-spin absolute right-4 top-1/2 -translate-y-1/2" size={14} style={{ color: "#7F1D1D" }} />
                        )}
                    </div>
                    {results.length > 0 && (
                        <div className="mt-2 rounded-2xl border overflow-hidden" style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }} data-testid="invite-search-results">
                            {results.map((u) => {
                                const chosen = selected.some((x) => x.id === u.id);
                                return (
                                    <button
                                        type="button"
                                        key={u.id}
                                        onClick={() => pickUser(u)}
                                        disabled={chosen}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#F5F0E6] disabled:opacity-50 disabled:bg-[#F5F0E6]"
                                        data-testid={`invite-search-result-${u.id}`}
                                    >
                                        <div className="h-8 w-8 rounded-full overflow-hidden flex items-center justify-center flex-shrink-0"
                                             style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                                            {u.photo_url ? <img src={u.photo_url} alt={u.name} className="h-full w-full object-cover" /> : <span className="text-sm">{initial(u.name)}</span>}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "#1E1B4B" }}>
                                                {u.name}
                                                {u.is_warrior ? (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" style={{ background: "#1E1B4B", color: "#D4AF37" }}>
                                                        <Flame size={9} /> Warrior
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" style={{ background: "#F5F0E6", color: "#7F1D1D" }}>
                                                        <User size={9} /> User
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs" style={{ color: "#525252" }}>{u.email_masked}</div>
                                        </div>
                                        {chosen ? <Check size={14} style={{ color: "#7F1D1D" }} /> : null}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {q.trim().length >= 2 && !searching && results.length === 0 && (
                        <div className="mt-2 text-xs" style={{ color: "#8a8a8a" }}>No matches. You can still invite by email below.</div>
                    )}
                    {selected.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="invite-selected-users">
                            {selected.map((u) => (
                                <span key={u.id} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border"
                                      style={{ borderColor: "#D4AF37", background: "#FDFBF7", color: "#1E1B4B" }}
                                      data-testid={`invite-selected-${u.id}`}>
                                    {u.is_warrior ? <Flame size={10} style={{ color: "#D4AF37" }} /> : <User size={10} style={{ color: "#7F1D1D" }} />}
                                    {u.name}
                                    <button onClick={() => removeSelected(u.id)} className="ml-1" aria-label="Remove">
                                        <X size={10} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                <div className="gold-divider my-6" />

                <form onSubmit={submit} data-testid="invite-form">
                    <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>
                        <Mail size={12} className="inline mr-1" /> {t("invite.emails_label")}
                    </label>
                    <textarea rows={2} value={emails}
                              onChange={(e) => setEmails(e.target.value)}
                              placeholder="friend@example.com, mom@example.com"
                              className="w-full px-4 py-3 rounded-2xl border mb-3 text-sm"
                              style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                              data-testid="invite-emails-input" />
                    <label className="block text-xs uppercase tracking-[0.22em] mb-1.5" style={{ color: "#7F1D1D" }}>{t("invite.message_label")}</label>
                    <textarea rows={2} value={message}
                              onChange={(e) => setMessage(e.target.value)}
                              className="w-full px-4 py-3 rounded-2xl border mb-3 text-sm"
                              style={{ borderColor: "#E5E0D8", background: "#FDFBF7" }}
                              data-testid="invite-message-input" />
                    {error && <div className="mb-2 text-sm" style={{ color: "#7F1D1D" }} data-testid="invite-error">{error}</div>}
                    <div className="flex items-center gap-3">
                        <button type="submit" disabled={sending} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="invite-send-button">
                            <Send size={14} /> {sending ? "…" : t("invite.send")}
                        </button>
                        {sent && (
                            <span className="text-sm inline-flex items-center gap-1" style={{ color: "#7F1D1D" }} data-testid="invite-sent">
                                <Check size={14} /> {t("invite.sent")}
                            </span>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
